/**
 * Public, closed vocabulary for staged statutory-liability rails. The canonical declarations are
 * the Prisma DB enums (regenerated into `./generated/enums.js`); re-exported here so rail
 * consumers keep importing from this domain module and the barrel does not carry two competing
 * declarations of the same names.
 *
 * `TaxAccrualOriginType` semantics:
 * - `HCM_PAYROLL_RUN` — payroll-run tax recognized by the HCM posting.
 * - `AR_INVOICE` — per-invoice own ISS recognized by the AR fiscal posting (ALC-1546).
 * - `STATUTORY_AUTO_PROVISION` — historical guide-created accrual; retained only to read and
 *   reverse legacy origins.
 * - `AP_BILL_WITHHOLDING` — invoice-triggered AP supplier withholding credited at bill
 *   approval (ALC-1547).
 * - `AP_VENDOR_PAYMENT` — PIS/COFINS/CSLL supplier retention recognized at vendor payment into
 *   Tax/Apurado (ALC-1548). Source event is the AP payment id.
 * - `AP_BILL_APPROVAL_LEGACY` — pre-feature open bill whose approval journal already credited
 *   the retention liability. Grandfathered so vendor payment does not post the withholding a
 *   second time (ALC-1548).
 * - `TAX_LIABILITY_RAIL_MIGRATION` — auditable opening balance reclassified from the selected
 *   liability parent into Apurado on a user-confirmed effective date.
 * - `AR_DEBIT_NOTE` / `CONTRACT_REVENUE_RECOGNITION` / `GL_JOURNAL` — Revenue Tax (PIS/COFINS)
 *   provision credited to Apurado with the revenue (ADR 0024, ALC-4409). `AR_INVOICE` is also used
 *   by the Revenue Tax provision of an invoice's revenue journal. `GL_JOURNAL` = a revenue journal
 *   with no AR / contract link (manual journal, import); the source event is the journal itself.
 */
export { TaxAccrualOriginType, TaxLiabilityRailStage } from './generated/enums.js';

/**
 * System-owned AP_BILL review for a BYPASS guide line that approved with unmatched
 * same-period origin principal (ALC-1543). Cannot be note-dismissed.
 */
export const UnmatchedStatutoryBypassRequestType = {
  UNMATCHED_STATUTORY_BYPASS: 'UNMATCHED_STATUTORY_BYPASS',
} as const;

export type UnmatchedStatutoryBypassRequestType =
  (typeof UnmatchedStatutoryBypassRequestType)[keyof typeof UnmatchedStatutoryBypassRequestType];

/** Closing-checklist / hard close-blocker system source for unmatched BYPASS principal. */
export const UNMATCHED_STATUTORY_BYPASS_CLOSE_SOURCE = 'UNMATCHED_STATUTORY_BYPASS' as const;

/** Review lifecycle surfaced on the statutory line card after staged approval. */
export const StatutoryUnmatchedReviewState = {
  NONE: 'NONE',
  PENDING: 'PENDING',
  RESOLVED: 'RESOLVED',
  CANCELLED: 'CANCELLED',
} as const;

export type StatutoryUnmatchedReviewState =
  (typeof StatutoryUnmatchedReviewState)[keyof typeof StatutoryUnmatchedReviewState];

/**
 * Structured `requestedChanges` payload for {@link UnmatchedStatutoryBypassRequestType}.
 * Amounts are frozen at approval; later self-heal appends `StatutoryGuideHealEvent` rows
 * and advances only review lifecycle fields.
 */
export interface UnmatchedStatutoryBypassReviewChanges {
  requestType: typeof UnmatchedStatutoryBypassRequestType.UNMATCHED_STATUTORY_BYPASS;
  billId: string;
  taxLineId: string;
  snapshotId: string;
  entityId: string;
  bookId: string;
  slotCode: string;
  assessmentPeriodYear: number;
  assessmentPeriodMonth: number;
  approvalJournalId: string;
  approvalApuradoAccountId: string;
  approvalGuidesPayableAccountId: string;
  /** Sum of eligible same-period origin balances available immediately before allocation. */
  sourceBalanceAmount: string;
  transferredAmount: string;
  /** Approval-time matched principal (frozen). */
  matchedAmount: string;
  /** Approval-time unmatched principal (frozen). */
  unmatchedAmount: string;
  functionalCurrency: string;
}

/** Public API response returned after configuring a staged tax-liability rail. */
export interface TaxLiabilityRailResponse {
  tenantId: string;
  railId: string;
  slotId: string;
  parentAccountId: string;
  apuradoAccountId: string;
  guidesPayableAccountId: string;
  /**
   * Pre-feature postable mapped account preserved as the temporary Legado child (ALC-1551).
   * Null for rails created by the earlier DRAFT-only conversion that flipped the mapped account
   * into the non-postable parent without a separate Legado child.
   */
  legadoAccountId: string | null;
  versionId: string;
}

/** @deprecated Prefer {@link TaxLiabilityRailResponse}. */
export type FgtsTaxLiabilityRailResponse = TaxLiabilityRailResponse;

/**
 * Payroll tax slots carrying the staged Apurado → Em Guias a Pagar lifecycle (ALC-1545).
 *
 * This is the single source of truth for payroll rail eligibility. Consumers must test
 * membership against it rather than re-listing slot codes; see ALC-1538 for the enabled set.
 *
 * `FGTS_RESCISORIO` is **guide-side only** today. The rail exists so a GRRF stops clearing the
 * monthly FGTS liability, but no payroll journal credits its Apurado stage yet: the per-event
 * TERMINATION journal carries no FGTS account or amount at all, `calculateTaxTotals` produces a
 * single undifferentiated `fgtsAmount`, and the rubric classifier deliberately excludes "multa"
 * from FGTS. Until a rescisório payroll credit exists, a GRRF always lands unmatched — that is the
 * designed outcome, not a defect. Tracked as the ALC-1545 follow-up.
 */
export const STAGED_PAYROLL_TAX_SLOT_CODES = [
  'INSS_FOLHA',
  'INSS_CONTRIBUINTE_INDIVIDUAL',
  'FGTS',
  'FGTS_RESCISORIO',
  'IRRF_SALARIOS',
] as const;

export type StagedPayrollTaxSlotCode = (typeof STAGED_PAYROLL_TAX_SLOT_CODES)[number];

export function isStagedPayrollTaxSlotCode(code: string): code is StagedPayrollTaxSlotCode {
  return (STAGED_PAYROLL_TAX_SLOT_CODES as readonly string[]).includes(code);
}

/**
 * Own-tax slots approved for the BR_CPC Apurado → Em Guias a Pagar lifecycle.
 *
 * The single source of truth. `@erp/services/tax-liability-rail` re-exports this array rather than
 * declaring a second literal, so a slot cannot be staged on one side of the boundary and not the
 * other.
 *
 * `IRPJ_RECOLHER`/`CSLL_RECOLHER` joined under the 2026-09-11 amendment to ADR 0002. Without them
 * `StatutoryProvisionPostingService.postStagedReclassification` filters an IRPJ/CSLL line out
 * through `isStagedTaxLiabilitySlot` and returns `NOT_ELIGIBLE_STAGED_LINE`, so the guia would
 * approve having posted nothing. Membership only makes a slot ELIGIBLE: `isTaxSlotRailEligible`
 * still requires an active mapping in the tenant's CoA version, so tenants that never mapped
 * IRPJ/CSLL are untouched, and those that did migrate through the existing operator-confirmed
 * Legado→Apurado flow (`TAX_LIABILITY_RAIL_MIGRATION_CONFIRMATION_REQUIRED`).
 */
export const STAGED_OWN_TAX_LIABILITY_SLOT_CODES = [
  'SIMPLES_NACIONAL',
  'PIS_FATURAMENTO',
  'COFINS_FATURAMENTO',
  'ICMS_PROPRIO',
  'ISS_PROPRIO',
  'IRPJ_RECOLHER',
  'CSLL_RECOLHER',
  'ICMS_ST',
  'CPRB_RECOLHER',
  'IRRF_OUTROS_RENDIMENTOS',
] as const;

export type StagedOwnTaxLiabilitySlotCode = (typeof STAGED_OWN_TAX_LIABILITY_SLOT_CODES)[number];

/**
 * Staged slots whose provision debit is recognized in the SAME journal as the guia approval.
 *
 * Every other staged own-tax slot is a tax on REVENUE. Its debit — a `DEDUCAO_*` deduction from
 * gross revenue — is recognized upstream when the AR invoice is issued, which is what creates the
 * `tax_accrual_origin` the guia later consumes. `AUTO_PROVISION` there is the escape hatch for a
 * missing origin: it moves Apurado → Em Guias a Pagar, deliberately leaves Apurado negative and
 * freezes the principal unmatched so period close must chase the recognition that never happened.
 *
 * IRPJ and CSLL are taxes on PROFIT, and Alchemy has no module that recognizes them. There is no
 * upstream event to chase, so the same escape hatch would leave Apurado permanently negative and
 * the income-tax expense permanently unrecognized — a misstatement, not an exception worth
 * reporting. For these slots approval posts the recognition first
 * (`Dr <provisionDebitSlotCode> / Cr Apurado`) and then the ordinary reclassification
 * (`Dr Apurado / Cr Em Guias a Pagar`), so Apurado nets to zero and the principal is fully matched.
 *
 * CPC 32 / IAS 12 put current tax expense in the period whose result produced it; CPC 26 (R1) /
 * IAS 1 and Lei 6.404/76 art. 187 (`V resultado antes do IR e a provisão para o imposto`) put it
 * on its own line below the operating result — which is why the paired slot is `*_EXPENSE` and
 * never `DEDUCAO_*`.
 *
 * Membership is read by BOTH the approval preview and the posting service, so the accounts shown
 * before approval are the accounts posted. Adding a slot here without a `provisionDebitSlotCode`
 * on its seed is a configuration error the posting service refuses.
 *
 * See `docs/adr/0002-statutory-bill-treatment-modes.md`, 2026-09-11 income-tax amendment.
 */
export const RECOGNIZE_PROVISION_AT_APPROVAL_SLOT_CODES = [
  'IRPJ_RECOLHER',
  'CSLL_RECOLHER',
] as const;

export type RecognizeProvisionAtApprovalSlotCode =
  (typeof RECOGNIZE_PROVISION_AT_APPROVAL_SLOT_CODES)[number];

export function shouldRecognizeProvisionAtApproval(
  code: string | null | undefined,
): code is RecognizeProvisionAtApprovalSlotCode {
  return (
    code != null && (RECOGNIZE_PROVISION_AT_APPROVAL_SLOT_CODES as readonly string[]).includes(code)
  );
}

export function isStagedOwnTaxLiabilitySlotCode(
  code: string,
): code is StagedOwnTaxLiabilitySlotCode {
  return (STAGED_OWN_TAX_LIABILITY_SLOT_CODES as readonly string[]).includes(code);
}

/**
 * HCM payroll system slot → fiscal tax slot it is an alias of (ALC-1538 decision, ALC-1545).
 *
 * Deliberately an exhaustive literal map of exactly four entries. Never derive this by
 * name-matching on `FGTS`/`INSS`: the provisioned encargos slots in
 * {@link HCM_PAYROLL_NON_ALIASED_PAYABLE_SLOT_CODES} also carry those tokens and must never
 * be routed onto a guide rail.
 */
export const HCM_PAYROLL_PAYABLE_TAX_SLOT_ALIASES = {
  HCM_PAYROLL_INSS_PAYABLE: 'INSS_FOLHA',
  HCM_PAYROLL_FGTS_PAYABLE: 'FGTS',
  HCM_PAYROLL_IRRF_PAYABLE: 'IRRF_SALARIOS',
  HCM_PAYROLL_FGTS_RESCISORIO_PAYABLE: 'FGTS_RESCISORIO',
} as const satisfies Record<string, StagedPayrollTaxSlotCode>;

export type AliasedHcmPayrollPayableSlotCode = keyof typeof HCM_PAYROLL_PAYABLE_TAX_SLOT_ALIASES;

export const ALIASED_HCM_PAYROLL_PAYABLE_SLOT_CODES = Object.keys(
  HCM_PAYROLL_PAYABLE_TAX_SLOT_ALIASES,
) as readonly AliasedHcmPayrollPayableSlotCode[];

/**
 * HCM payable slots that look tax-like but are provisioned encargos or net-pay liabilities.
 * They keep their own independently configurable accounts and never join a guide rail.
 */
export const HCM_PAYROLL_NON_ALIASED_PAYABLE_SLOT_CODES = [
  'HCM_PAYROLL_LIABILITY',
  'HCM_PAYROLL_VACATION_PAYABLE',
  'HCM_PAYROLL_THIRTEENTH_PAYABLE',
  'HCM_PAYROLL_VACATION_CHARGES_PAYABLE',
  'HCM_PAYROLL_THIRTEENTH_CHARGES_PAYABLE',
  'HCM_PAYROLL_VACATION_CHARGES_INSS_PAYABLE',
  'HCM_PAYROLL_VACATION_CHARGES_FGTS_PAYABLE',
  'HCM_PAYROLL_THIRTEENTH_CHARGES_INSS_PAYABLE',
  'HCM_PAYROLL_THIRTEENTH_CHARGES_FGTS_PAYABLE',
  'HCM_PAYROLL_EMPLOYEE_ADVANCE',
  'HCM_PAYROLL_DEDUCTIONS_PAYABLE',
  'HCM_PAYROLL_CONSIGNED_PAYABLE',
  'HCM_PAYROLL_UNION_PAYABLE',
  'HCM_PAYROLL_ALIMONY_PAYABLE',
] as const;

export function isAliasedHcmPayrollPayableSlotCode(
  code: string,
): code is AliasedHcmPayrollPayableSlotCode {
  return Object.hasOwn(HCM_PAYROLL_PAYABLE_TAX_SLOT_ALIASES, code);
}

/** Fiscal tax slot a HCM payable slot aliases, or `null` when it owns its own liability. */
export function resolveAliasedTaxSlotCode(hcmSlotCode: string): StagedPayrollTaxSlotCode | null {
  return isAliasedHcmPayrollPayableSlotCode(hcmSlotCode)
    ? HCM_PAYROLL_PAYABLE_TAX_SLOT_ALIASES[hcmSlotCode]
    : null;
}

/**
 * AP supplier-withholding tax slots carrying the staged Apurado → Em Guias a Pagar lifecycle
 * (ALC-1547). Their origin is the withholding line an AP bill posts at approval — the legal
 * trigger for all six is invoice-side (accounting credit / service competence / municipality
 * rule), never the later vendor payment.
 *
 * `PIS_RETIDO`, `COFINS_RETIDO`, and `CSLL_RETIDO` are deliberately excluded: those three are
 * ALC-1548's payment-triggered rails (the ADR's grandfathering clause moves their withholding
 * recognition to vendor payment). Staging them here would double-recognize the liability — once
 * at this ticket's approval-time credit, again at ALC-1548's payment-time credit.
 */
export const STAGED_AP_WITHHOLDING_TAX_SLOT_CODES = [
  'IRRF_RETIDO_SERVICOS_PJ',
  'IRRF_RETIDO_SERVICOS_PF',
  'IRRF_RETIDO_ALUGUEIS',
  'IRRF_RETIDO_ALUGUEIS_PJ',
  'INSS_RETIDO_SERVICOS',
  'ISS_RETIDO',
] as const;

export type StagedApWithholdingTaxSlotCode = (typeof STAGED_AP_WITHHOLDING_TAX_SLOT_CODES)[number];

export function isStagedApWithholdingTaxSlotCode(
  code: string,
): code is StagedApWithholdingTaxSlotCode {
  return (STAGED_AP_WITHHOLDING_TAX_SLOT_CODES as readonly string[]).includes(code);
}

/**
 * Supplier retentions whose legal recognition trigger is vendor payment (ALC-1548).
 *
 * They deliberately remain outside {@link STAGED_AP_WITHHOLDING_TAX_SLOT_CODES}, because that
 * list controls invoice-approval origin production. They still participate in the same staged
 * liability rail and therefore belong in the combined rail vocabulary.
 */
export const PAYMENT_TRIGGERED_SUPPLIER_RETENTION_SLOT_CODES = [
  'PIS_RETIDO',
  'COFINS_RETIDO',
  'CSLL_RETIDO',
] as const;

export type PaymentTriggeredSupplierRetentionSlotCode =
  (typeof PAYMENT_TRIGGERED_SUPPLIER_RETENTION_SLOT_CODES)[number];

export function isPaymentTriggeredSupplierRetentionSlotCode(
  code: string,
): code is PaymentTriggeredSupplierRetentionSlotCode {
  return (PAYMENT_TRIGGERED_SUPPLIER_RETENTION_SLOT_CODES as readonly string[]).includes(code);
}

/**
 * Every tax slot carrying the staged rail lifecycle today. ALC-1545 enables the payroll rails,
 * ALC-1546/ALC-1538 the own-tax rails, ALC-1547 the invoice-triggered AP-withholding rails, and
 * ALC-1548 the payment-triggered supplier-retention rails — consumers gate on this predicate
 * rather than on any single category list directly.
 */
export function isStagedTaxLiabilityRailSlotCode(code: string): boolean {
  return (
    isStagedPayrollTaxSlotCode(code) ||
    isStagedOwnTaxLiabilitySlotCode(code) ||
    isStagedApWithholdingTaxSlotCode(code) ||
    isPaymentTriggeredSupplierRetentionSlotCode(code)
  );
}

/**
 * Union of every staged-rail-eligible slot code, for callers that must ensure/validate every
 * managed rail regardless of category (CoA-version configuration, publication and activation
 * guards). Prefer {@link isStagedTaxLiabilityRailSlotCode} for membership checks; use this only
 * where the caller needs to iterate the full set.
 */
export const ALL_STAGED_TAX_LIABILITY_SLOT_CODES = [
  ...STAGED_PAYROLL_TAX_SLOT_CODES,
  ...STAGED_OWN_TAX_LIABILITY_SLOT_CODES,
  ...STAGED_AP_WITHHOLDING_TAX_SLOT_CODES,
  ...PAYMENT_TRIGGERED_SUPPLIER_RETENTION_SLOT_CODES,
] as const;

/** Read-only evidence for adopting a shared managed liability destination. */
export interface TaxLiabilityRailAdoptionPreview {
  fingerprint: string;
  action: 'CONFIGURE' | 'REUSE_SHARED' | 'ALREADY_CONFIGURED';
  shared: boolean;
  historicalReviewRequired: boolean;
  selectedAccount: { glAccountId: string; code: string; name: string };
  affectedMappings: {
    slotId: string;
    slotCode: string;
    slotName: string;
    entityId: string | null;
    glAccountId: string;
  }[];
  scopes: {
    entityId: string;
    bookId: string;
    functionalCurrency: string;
    balances: { glAccountId: string; amount: string }[];
    journalCount: number;
    originCount: number;
    allocationCount: number;
    unpaidRetentionAmount: string;
    snapshotCount: number;
  }[];
  changes: {
    preservesJournalEntries: true;
    preservesSourceAccountId: string;
    createsHierarchy: boolean;
    migratesBalance: boolean;
  };
}

/** Batch preview captures all selected destinations before any membership changes. */
export interface TaxLiabilityRailAdoptionBatchPreview {
  rails: { slotCode: string; preview: TaxLiabilityRailAdoptionPreview }[];
}

/** Stable public configuration diagnostics shared by preview and execution. */
export const TAX_LIABILITY_RAIL_ERROR = {
  /** The slot is not an eligible credit-balance, tax-slip-payable liability slot. */
  SLOT_NOT_ELIGIBLE: 'TAX_RAIL_SLOT_NOT_ELIGIBLE',
  /** The referenced chart-of-accounts version does not exist. */
  COA_VERSION_NOT_FOUND: 'TAX_RAIL_COA_VERSION_NOT_FOUND',
  /** The CoA version's status does not permit ensuring a staged hierarchy on it. */
  COA_VERSION_NOT_ENSURABLE: 'TAX_RAIL_COA_VERSION_NOT_ENSURABLE',
  /** The slot is not mapped to an active, postable, credit-balance liability. */
  SLOT_MAPPING_INVALID: 'TAX_RAIL_SLOT_MAPPING_INVALID',
  /** The mapped account already has children and cannot be wrapped as Legado. */
  LEGADO_HAS_CHILDREN: 'TAX_RAIL_LEGADO_HAS_CHILDREN',
  /** "Em Guias a Pagar" must open at zero; a balance is already sitting there. */
  GUIDES_BALANCE_NOT_ZERO: 'TAX_RAIL_GUIDES_BALANCE_NOT_ZERO',
  /** An alias slot points at an account outside this rail. */
  ALIAS_OUTSIDE_RAIL: 'TAX_RAIL_ALIAS_OUTSIDE_RAIL',
  /**
   * No staged hierarchy is configured for this slot in this CoA version.
   *
   * Spelled to match `BillStatutoryTreatmentService`'s existing `blockerCode`
   * rather than given a `TAX_RAIL_` prefix: the web client already switches on
   * this exact string, so the execution path and the preview path now name the
   * same condition the same way.
   */
  STAGED_RAIL_NOT_CONFIGURED: 'STAGED_RAIL_NOT_CONFIGURED',
  /** The rail exists but its accounts are incomplete, inactive or mis-parented. */
  STAGED_ACCOUNTS_UNUSABLE: 'TAX_RAIL_STAGED_ACCOUNTS_UNUSABLE',
  /** Incompatible physical ownership or stage overlap; compatible members may share a structure. */
  ACCOUNT_ALREADY_CLAIMED: 'TAX_LIABILITY_RAIL_ACCOUNT_ALREADY_CLAIMED',
  /**
   * The slot maps a non-postable group account whose rail row is missing (a half-built rail).
   * Only the half-built repair can configure it; posting and the plain map flow refuse it.
   */
  SETUP_REQUIRED: 'TAX_LIABILITY_RAIL_SETUP_REQUIRED',
  /** The accounts picked for a half-built repair are not the group account's two stage children. */
  REPAIR_ACCOUNTS_INVALID: 'TAX_LIABILITY_RAIL_REPAIR_ACCOUNTS_INVALID',
  /** The taxes that share the group account changed after the repair dialog was opened. */
  REPAIR_STALE: 'TAX_LIABILITY_RAIL_REPAIR_STALE',
  /**
   * The user may not see every entity on the CoA version, or a journal on the group's accounts.
   * Decided from the user's scope before any balance is read, so it discloses no hidden data.
   */
  REPAIR_SCOPE_REQUIRED: 'TAX_LIABILITY_RAIL_REPAIR_SCOPE_REQUIRED',
} as const;

/** `CREATE` picks the two stage children; `JOIN` reuses the stages of the rail that owns the parent. */
export type HalfBuiltTaxLiabilityRailMode = 'CREATE' | 'JOIN';

/**
 * A slot mapped to a non-postable group account with no rail row (see
 * {@link TAX_LIABILITY_RAIL_ERROR.SETUP_REQUIRED}). `CREATE` repairs pick the two stage
 * children; `JOIN` repairs reuse the stages of a rail that already owns the group account.
 */
export interface HalfBuiltTaxLiabilityRailResponse {
  mode: HalfBuiltTaxLiabilityRailMode;
  parent: { glAccountId: string; code: string; name: string };
  children: { glAccountId: string; code: string; name: string }[];
  /** Every eligible tax mapped to the group account without a rail row; one repair fixes all. */
  slots: { slotId: string; slotCode: string; slotName: string }[];
  suggestedApuradoAccountId: string | null;
  suggestedGuidesPayableAccountId: string | null;
  /** The refusal the repair would give for the suggested accounts, or null when it would apply. */
  blocker: { code: string; message: string; accountCode?: string } | null;
}

export interface RepairHalfBuiltTaxLiabilityRailRequest {
  apuradoAccountId: string;
  guidesPayableAccountId: string;
  /** The slot codes the dialog showed; the repair refuses with REPAIR_STALE when they changed. */
  slotCodes: string[];
}

export interface RepairHalfBuiltTaxLiabilityRailResponse {
  /** `ALREADY_CONFIGURED` when the slot already had its rail row: nothing was written. */
  outcome: 'REPAIRED' | 'ALREADY_CONFIGURED';
  /** How the repair wrote the rows; null when nothing was written. */
  mode: HalfBuiltTaxLiabilityRailMode | null;
  parentAccountId: string;
  apuradoAccountId: string;
  guidesPayableAccountId: string;
  /** Every slot that now has a rail row on this group account. */
  slotCodes: string[];
}

export type TaxLiabilityRailErrorCode =
  (typeof TAX_LIABILITY_RAIL_ERROR)[keyof typeof TAX_LIABILITY_RAIL_ERROR];
